import { NgClass } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, DestroyRef, Input, OnInit, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { CalioTelInputComponent } from '@calio-lib/calio-intl-tel-input/calio-tel-input/calio-tel-input.component';
import { PasswordStrengthBarComponent } from '@components/password-strength-bar/password-strength-bar.component';
import { COMPANY_SIZE, INDUSTRY_LIST, PARTNER_REGISTRATION_MODE, PartnerRegistrationMode } from '@constants/app.constants';
import { environment } from '@environments/environment';
import { PartnerFormModel } from '@models/partner-form.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { AddRequiredPipe } from '@pipe/add-required.pipe';
import { StaticTranslationSortPipe } from '@pipe/static-translation-sort.pipe';
import { HelperService } from '@services/helper.service';
import { HttpClientService } from '@services/http-client.service';
import { LoggerService } from '@services/logger.service';
import { SignupRequirementsService } from '@services/signup-requirements.service';

@Component({
  selector: 'app-personal-data-step',
  templateUrl: './personal-data-step.component.html',
  styleUrls: ['./personal-data-step.component.scss'],
  imports: [FormsModule, NgClass, NgSelectModule, CalioTelInputComponent, PasswordStrengthBarComponent, TranslateModule, AddRequiredPipe, StaticTranslationSortPipe]
})
export class PersonalDataStepComponent implements OnInit {

  @Input() email: string;
  @Input('personalFormError') set personalForm(reset: boolean) {
    reset && this.resetPersnalDataForm();
  };
  @Input() registrationMode: PartnerRegistrationMode = PARTNER_REGISTRATION_MODE.PLG;
  // Backend said a mobile is required (mobile.required) — force the phone field
  // to render in SLG mode even if the async /requirements never resolved
  // require_mobile_confirmation, so the number can always be entered. JB-1569.
  @Input() mobileRequired = false;
  // JB-1686: pre-fill the form when the user returns from an OTP screen via
  // "wrong email/number? edit", so nothing has to be re-typed. Seeded in ngOnInit.
  @Input() prefill?: PartnerFormModel;

  protected onNextEvent = output<PartnerFormModel>();

  private destroyRef = inject(DestroyRef);
  private helperService = inject(HelperService);
  private httpClientService = inject(HttpClientService);
  private signupRequirementsService = inject(SignupRequirementsService);

  // Lowercase ISO-2 countries the phone input may offer (from the brand's
  // signup policy); empty = every country.
  protected readonly allowedCountries = this.signupRequirementsService.allowedCountries;

  // Whether the brand requires mobile verification. In SLG mode the form is
  // email + password only, so the phone field is shown only when this is on
  // (PLG always shows it). JB-1569.
  protected readonly requireMobileConfirmation = this.signupRequirementsService.requireMobileConfirmation;

  protected readonly redirectUrl = environment.redirectDomain;
  protected readonly industryList = INDUSTRY_LIST;
  protected readonly companySize = COMPANY_SIZE;

  protected partnerFormModel = new PartnerFormModel();
  protected showPasswordDetails: boolean;
  protected accountCreated$ = signal(false);
  protected showLoader$ = signal(false);
  protected showText$ = signal(true);
  protected isPasswordValid$ = signal(false);
  protected emailError$ = signal<'unique' | 'banned' | 'disposable' | 'free_provider' | 'domain_blocked' | null>(null);

  protected get isSLGMode(): boolean {
    return this.registrationMode === PARTNER_REGISTRATION_MODE.SLG;
  }

  private validateEmailAndSubmit(result: PartnerFormModel): void {
    this.emailError$.set(null);

    this.httpClientService.post('partners/check_email', { email: this.partnerFormModel.email })
      .subscribe({
        next: () => {
          this.onNextEvent.emit(result);
        },
        error: (error: HttpErrorResponse) => {
          this.showLoader$.set(false);
          this.showText$.set(true);
          if (error.status === HttpStatusCode.Conflict) {
            this.emailError$.set('unique');
          } else if (error.status === HttpStatusCode.Forbidden) {
            // Signup Rules P2 (email hardening): map the specific policy reason
            // instead of always showing the generic banned-domain message.
            const errorCode = error.error?.errors?.[0]?.error_code;
            this.emailError$.set(
              errorCode === 'disposableEmail' ? 'disposable'
                : errorCode === 'freeEmail' ? 'free_provider'
                  : errorCode === 'domainBlocked' ? 'domain_blocked'
                    : errorCode === 'domainNotAllowed' ? 'domain_blocked'
                      : 'banned'
            );
          }
          LoggerService.error(error);
        }
      });
  }

  ngOnInit(): void {
    // JB-1686: seed from the parent's model first (so an edit-and-return keeps
    // everything the user already typed), then fill any gaps from the IP lookup.
    if (this.prefill) {
      Object.assign(this.partnerFormModel, this.prefill);
    }
    this.getUserCountry();
  }

  protected resetPersnalDataForm(): void {
    this.showLoader$.set(false);
    this.showText$.set(true);
  }

  private getUserCountry(): void {
    this.helperService.getUserCountry()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: userdata => {
          if (userdata?.country) {
            this.partnerFormModel.country_identifier = userdata.country.toLowerCase();
            this.partnerFormModel.timezone = userdata.timezone;
            this.partnerFormModel.country_code = userdata.country;
          }
        },
        error: (error: HttpErrorResponse) => {
          LoggerService.warn("IP lookup service throwing error");
          LoggerService.error(error);
        }
      });
  }

  protected savePartnerData(): void {
    this.showLoader$.set(true);
    this.showText$.set(false);

    const result: PartnerFormModel = {
      name: this.partnerFormModel.name,
      email: this.isSLGMode ? this.partnerFormModel.email : this.email,
      prename: this.partnerFormModel.prename,
      lastname: this.partnerFormModel.lastname,
      job_title: this.partnerFormModel.job_title,
      password: this.partnerFormModel.password,
      password_repeat: this.partnerFormModel.password,
      country_identifier: this.partnerFormModel.country_identifier,
      country_code: this.partnerFormModel.country_code,
      timezone: this.partnerFormModel.timezone,
      industry: this.partnerFormModel.industry,
      company_size: this.partnerFormModel.company_size,
      phone: this.partnerFormModel?.phone || undefined
    };

    // SLG mode: Validate email first, then submit
    if (this.isSLGMode) {
      this.validateEmailAndSubmit(result);
    } else {
      // PLG mode: Email was already validated in step 1
      this.onNextEvent.emit(result);
    }
  }

  protected validatePassword(event: { score: number, idx: number, col: string }): void {
    this.isPasswordValid$.set((event.score >= 90));
  }
}
